import { useEffect, useState, useMemo } from "react";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

function fmtHumidite(v, digits = 4) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(digits)} %`;
}

export default function ConsultationConditionnement() {
  const { t, ui } = useI18n();

  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState("");
  const [details, setDetails]   = useState(null);

  // Edit masses conditionnement
  const [editMode, setEditMode]         = useState(false);
  const [editTarget, setEditTarget]     = useState(null); // { rangee, stackIdx }
  const [editMasses, setEditMasses]     = useState({});   // { position: masse }
  const [saving, setSaving]             = useState(false);

  const [filters, setFilters] = useState({
    search: "", stack_id: "", operateur: "", enceinte: "",
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  // ── Chargement ────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch("/api/conditionnement?per_page=500");
        const j = await r.json();
        setRows(j.data || []);
      } catch {
        setErr("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Filtres ───────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let result = [...rows];
    if (filters.search) {
      const s = filters.search.toLowerCase();
      result = result.filter(r =>
        (r.time_display || "").toLowerCase().includes(s) ||
        (r.Operateur || "").toLowerCase().includes(s) ||
        (r.NumEnceinte || "").toLowerCase().includes(s) ||
        [...(r.RangeeA || []), ...(r.RangeeB || [])].some(st =>
          st.StackId.toLowerCase().includes(s)
        )
      );
    }
    if (filters.stack_id) {
      result = result.filter(r =>
        [...(r.RangeeA || []), ...(r.RangeeB || [])].some(st =>
          st.StackId.toLowerCase().includes(filters.stack_id.toLowerCase())
        )
      );
    }
    if (filters.operateur)
      result = result.filter(r => (r.Operateur || "").toLowerCase().includes(filters.operateur.toLowerCase()));
    if (filters.enceinte)
      result = result.filter(r => (r.NumEnceinte || "").toLowerCase().includes(filters.enceinte.toLowerCase()));

    if (sortConfig.key) {
      const { key, direction } = sortConfig;
      result = [...result].sort((a, b) => {
        const aV = a[key] ?? ""; const bV = b[key] ?? "";
        if (!isNaN(aV) && !isNaN(bV))
          return direction === "asc" ? Number(aV) - Number(bV) : Number(bV) - Number(aV);
        return direction === "asc"
          ? String(aV).localeCompare(String(bV))
          : String(bV).localeCompare(String(aV));
      });
    }
    return result;
  }, [rows, filters, sortConfig]);

  const totalPages    = Math.ceil(filteredRows.length / PAGE_SIZE);
  const paginatedRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setCurrentPage(1); };
  const resetFilters = () => { setFilters({ search: "", stack_id: "", operateur: "", enceinte: "" }); setSortConfig({ key: null, direction: "asc" }); setCurrentPage(1); };
  const getSortIcon = (k) => sortConfig.key !== k
    ? <span style={S.sortIcon}>⇅</span>
    : <span style={{ ...S.sortIcon, color: "#E6FF3E" }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>;
  const handleSort = (k) => setSortConfig({ key: k, direction: sortConfig.key === k && sortConfig.direction === "asc" ? "desc" : "asc" });

  // ── Stats header ──────────────────────────────────────────────────────
  const globalStats = useMemo(() => ({
    total:     rows.length,
    nbStacks:  rows.reduce((a, r) => a + (r.NbStacks || 0), 0),
    nbPlis:    rows.reduce((a, r) => a + (r.NbPlisTotal || 0), 0),
  }), [rows]);

  // ── Edit masses ───────────────────────────────────────────────────────
  const openEditMasses = (rangee, stackObj) => {
    const init = {};
    // Plis avec masses existantes
    (stackObj.Plis || []).forEach(p => { init[p.Position] = p.Masse ?? ""; });
    // Si pas de Plis mais Plis_Info, on init à vide
    if (!(stackObj.Plis || []).length) {
      (stackObj.Plis_Info || []).forEach(p => { init[p.position] = ""; });
    }
    setEditTarget({ rangee, stackId: stackObj.StackId });
    setEditMasses(init);
    setEditMode(true);
  };

  const handleSaveMasses = async () => {
    if (!details || !editTarget) return;
    setSaving(true);
    try {
      const rows_payload = Object.entries(editMasses)
        .map(([pos, masse]) => ({ Position: parseInt(pos), Masse: parseFloat(masse) }))
        .filter(r => !isNaN(r.Masse) && r.Masse > 0);

      const resp = await apiFetch("/api/traitement-thermique/update-masse-cond", {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          time:    details.time,
          stackId: editTarget.stackId,
          rangee:  editTarget.rangee,
          rows:    rows_payload,
        }),
      });
      const out = await resp.json();
      if (out.status !== "success") { alert(out.message || "Erreur"); return; }

      // Mise à jour state local
      const updateRangee = (rangeeList) =>
        rangeeList.map(s =>
          s.StackId === editTarget.stackId
            ? {
                ...s,
                Plis: out.plis ?? s.Plis,
                Masse: out.masse != null ? out.masse : s.Masse,
                HumiditeApresConditionnement:
                  out.humiditeApresConditionnement ?? s.HumiditeApresConditionnement,
                PlisHumidite: out.plisHumidite ?? s.PlisHumidite,
              }
            : s
        );

      setRows(prev => prev.map(session => {
        if (session.time !== details.time) return session;
        return {
          ...session,
          [editTarget.rangee]: updateRangee(session[editTarget.rangee] || []),
        };
      }));
      setDetails(prev => ({
        ...prev,
        [editTarget.rangee]: updateRangee(prev[editTarget.rangee] || []),
      }));

      setEditMode(false);
      setEditTarget(null);
      setEditMasses({});
    } catch { alert("Erreur réseau"); }
    finally { setSaving(false); }
  };

  // ── Export CSV ────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (!filteredRows.length) return;
    const headers = ["Date", "Fin", "Opérateur", "Enceinte", "Température (°C)", "Durée", "Nb Stacks", "Nb Plis", "Stacks RangeeA", "Stacks RangeeB"];
    const csvRows = filteredRows.map(r => [
      r.time_display, r.CompletedAt, r.Operateur, r.NumEnceinte,
      r.Temperature ?? "", r.Duree ?? "", r.NbStacks, r.NbPlisTotal,
      (r.RangeeA || []).map(s => s.StackId).join(";"),
      (r.RangeeB || []).map(s => s.StackId).join(";"),
    ]);
    const csv = [headers, ...csvRows].map(row =>
      row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    a.download = `conditionnement_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // ── Render rangée dans modal ──────────────────────────────────────────
  const renderRangee = (rangeeKey, rangeeList) => {
    if (!rangeeList || rangeeList.length === 0) return null;
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={S.rangeeBadge}>{rangeeKey === "RangeeA" ? "🅰 Rangée A" : "🅱 Rangée B"}</div>
        {rangeeList.map((stack, si) => {
          const isEditing = editMode && editTarget?.rangee === rangeeKey && editTarget?.stackId === stack.StackId;
          const hStack = stack.HumiditeApresConditionnement;
          // Merge Plis_Info + Plis masses + humidités pli
          const plisMap = {};
          (stack.Plis || []).forEach(p => { plisMap[p.Position] = p.Masse; });
          const humMap = {};
          (stack.PlisHumidite || []).forEach(ph => {
            humMap[ph.Position] = ph.Humidite;
          });
          let plisDisplay;
          if ((stack.Plis_Info || []).length) {
            plisDisplay = (stack.Plis_Info || []).map(pi => ({
              position: pi.position,
              plyPn: pi.plyPn,
              masse: plisMap[pi.position] ?? null,
              humidite: humMap[pi.position] ?? null,
            }));
          } else if ((stack.PlisHumidite || []).length) {
            plisDisplay = (stack.PlisHumidite || []).map(ph => ({
              position: ph.Position,
              plyPn: ph.PlyPn,
              masse: plisMap[ph.Position] ?? null,
              humidite: ph.Humidite,
            }));
          } else {
            plisDisplay = (stack.Plis || []).map(p => ({
              position: p.Position,
              plyPn: "—",
              masse: p.Masse,
              humidite: humMap[p.Position] ?? null,
            }));
          }
          plisDisplay.sort((a, b) => (a.position || 0) - (b.position || 0));

          return (
            <div key={si} style={S.stackBlock}>
              {/* Header stack */}
              <div style={S.stackHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={S.stackIdBadge}>{stack.StackId}</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>{stack.NbPlis} plis</span>
                  <span style={{
                    ...S.statusBadge,
                    background: stack.Status === "termine" ? "rgba(74,222,128,0.15)" : "rgba(251,191,36,0.15)",
                    color:      stack.Status === "termine" ? "#4ade80" : "#fbbf24",
                    border:     `1px solid ${stack.Status === "termine" ? "rgba(74,222,128,0.3)" : "rgba(251,191,36,0.3)"}`,
                  }}>
                    {stack.Status === "termine" ? "✓ Terminé" : stack.Status}
                  </span>
                  {stack.Masse != null && stack.Masse > 0 &&
                    <span style={{ fontSize: 12, color: "#60a5fa" }}>⚖️ {Number(stack.Masse).toFixed(2)} g</span>
                  }
                </div>
                {/* Bouton modifier */}
                {!isEditing ? (
                  <button className="woodoo-edit"
                    style={{ ...S.voirBtn, background: "#60a5fa", color: "#fff", fontSize: 11, padding: "5px 12px" }}
                    onClick={() => openEditMasses(rangeeKey, stack)}>
                    ✏️ Modifier masses
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={{ ...S.voirBtn, background: "#4ade80", color: "#000", fontSize: 11, padding: "5px 12px", opacity: saving ? 0.6 : 1 }}
                      onClick={handleSaveMasses} disabled={saving}>
                      {saving ? "Enregistrement…" : "✓ Enregistrer"}
                    </button>
                    <button style={{ ...S.voirBtn, background: "rgba(255,255,255,0.1)", color: "#cbd5e1", fontSize: 11, padding: "5px 12px" }}
                      onClick={() => { setEditMode(false); setEditTarget(null); setEditMasses({}); }} disabled={saving}>
                      {ui("Annuler")}
                    </button>
                  </div>
                )}
              </div>

              <div style={S.humStackBar}>
                <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600, flex: 1 }}>
                  Plis du stack · humidités après conditionnement
                </span>
              </div>

              {/* Tableau plis */}
              <div style={{ ...S.tableWrap, marginTop: 10 }} className="woodoo-scroll">
                <table style={S.table}>
                  <thead>
                    <tr>
                      {["Pos.", "Pli PN", "Masse cond. (g)", "H stack (%)", "H pli (%)"].map(h => (
                        <th key={h} className="woodoo-th" style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plisDisplay.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ ...S.td, color: "#64748b", fontStyle: "italic" }}>
                          Aucun pli enregistré
                        </td>
                      </tr>
                    ) : plisDisplay.map((pli, i) => (
                      <tr key={i} className="woodoo-tr" style={i % 2 === 0 ? S.trEven : S.trOdd}>
                        <td style={S.tdIndex}>{pli.position}</td>
                        <td style={{ ...S.td, ...S.tdStack, textAlign: "left" }}>{pli.plyPn || "—"}</td>
                        <td style={S.td}>
                          {isEditing ? (
                            <input
                              className="woodoo-input"
                              type="number" min="0" step="0.01"
                              value={editMasses[pli.position] ?? ""}
                              onChange={e => setEditMasses(prev => ({ ...prev, [pli.position]: e.target.value }))}
                              style={{
                                width: 120, padding: "5px 8px", borderRadius: 6,
                                fontSize: 12, fontWeight: 700, textAlign: "center",
                                borderColor: String(editMasses[pli.position]) !== String(pli.masse ?? "")
                                  ? "#E6FF3E" : "#1e3a50",
                                boxShadow: String(editMasses[pli.position]) !== String(pli.masse ?? "")
                                  ? "0 0 0 2px rgba(230,255,62,0.2)" : "none",
                              }}
                            />
                          ) : pli.masse != null
                            ? <span style={{ color: "#60a5fa", fontWeight: 700 }}>{Number(pli.masse).toFixed(2)} g</span>
                            : <span style={{ color: "#475569", fontStyle: "italic" }}>—</span>
                          }
                        </td>
                        {i === 0 && (
                          <td
                            rowSpan={plisDisplay.length}
                            style={{
                              ...S.td,
                              color: "#72b7b2",
                              fontWeight: 900,
                              fontSize: 15,
                              verticalAlign: "middle",
                              borderRight: "1px solid #1e3a50",
                              background: "rgba(114,183,178,0.06)",
                            }}
                          >
                            {fmtHumidite(hStack)}
                          </td>
                        )}
                        <td style={{ ...S.td, color: "#a7e0dc", fontWeight: 800 }}>
                          {fmtHumidite(pli.humidite)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <style>{globalCSS}</style>
      <div style={S.page}>
        <MenuBar active="production" />
        <div style={S.wrap}>

          {/* ── Header ── */}
          <div style={S.header}>
            <div>
              <div style={S.headerTitle}>Conditionnement</div>
              <div style={S.headerSub}>Historique des sessions & masses par pli</div>
            </div>
            <div style={S.statsRow}>
              <div style={S.statCard}>
                <div style={S.statNum}>{globalStats.total}</div>
                <div style={S.statLabel}>Sessions</div>
              </div>
              <div style={{ ...S.statCard, borderColor: "#E6FF3E" }}>
                <div style={{ ...S.statNum, color: "#E6FF3E" }}>{globalStats.nbStacks}</div>
                <div style={S.statLabel}>Stacks</div>
              </div>
              <div style={{ ...S.statCard, borderColor: "#60a5fa" }}>
                <div style={{ ...S.statNum, color: "#60a5fa" }}>{globalStats.nbPlis}</div>
                <div style={S.statLabel}>Plis traités</div>
              </div>
            </div>
          </div>

          {/* ── Panel ── */}
          <div style={S.panel}>
            <div style={S.filterBar}>
              {[
                { key: "search",    placeholder: "Recherche globale",  icon: "🔍" },
                { key: "stack_id",  placeholder: "ID Stack",           icon: "🔖" },
                { key: "operateur", placeholder: "Opérateur",          icon: "👤" },
                { key: "enceinte",  placeholder: "Enceinte (E01...)",  icon: "🏭" },
              ].map(({ key, placeholder, icon }) => (
                <div key={key} style={S.filterWrap}>
                  <span style={S.filterIcon}>{icon}</span>
                  <input className="woodoo-input" type="text" placeholder={placeholder}
                    value={filters[key]} onChange={e => setFilter(key, e.target.value)}
                    style={S.filterInput} />
                </div>
              ))}
              <button className="woodoo-btn-lime" style={S.exportBtn} onClick={exportCSV}>📥 CSV</button>
              <button className="woodoo-btn-reset" style={S.resetBtn} onClick={resetFilters}>✕ Reset</button>
            </div>

            <div style={S.resultCount}>
              <span style={S.resultDot} />
              {filteredRows.length} résultat(s) sur {rows.length}
            </div>

            {loading && <div style={S.loading}><span className="woodoo-spin">⟳</span> {ui("Chargement…")}</div>}
            {err     && <div style={S.errMsg}>{err}</div>}

            {!loading && (
              <div style={S.tableWrap} className="woodoo-scroll">
                <table style={S.table}>
                  <thead>
                    <tr>
                      {[
                        ["time_display",  "Date"],
                        ["Operateur",     "Opérateur"],
                        ["NumEnceinte",   "Enceinte"],
                        ["Temperature",   "Temp. (°C)"],
                        ["Duree",         "Durée (h)"],
                        ["NbStacks",      "Nb Stacks"],
                        ["NbPlisTotal",   "Nb Plis"],
                        ["Status",        "Statut"],
                      ].map(([key, label]) => (
                        <th key={key} className="woodoo-th" style={S.th} onClick={() => handleSort(key)}>
                          {label} {getSortIcon(key)}
                        </th>
                      ))}
                      <th style={{ ...S.th, cursor: "default" }}>Stacks</th>
                      <th style={{ ...S.th, cursor: "default" }}>Détail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((r, i) => {
                      const allStacks = [...(r.RangeeA || []), ...(r.RangeeB || [])];
                      return (
                        <tr key={`${r.time}-${i}`} className="woodoo-tr" style={i % 2 === 0 ? S.trEven : S.trOdd}>
                          <td style={S.td}>{r.time_display || "—"}</td>
                          <td style={S.td}>{r.Operateur || "—"}</td>
                          <td style={{ ...S.td, color: "#E6FF3E", fontWeight: 700 }}>{r.NumEnceinte || "—"}</td>
                          <td style={S.td}>{r.Temperature != null ? `${r.Temperature} °C` : "—"}</td>
                          <td style={S.td}>{r.Duree != null ? r.Duree : "—"}</td>
                          <td style={S.td}>{r.NbStacks}</td>
                          <td style={S.td}>{r.NbPlisTotal}</td>
                          <td style={S.td}>
                            <span style={{
                              ...S.statusBadge,
                              background: r.Status === "termine" ? "rgba(74,222,128,0.15)" : "rgba(251,191,36,0.15)",
                              color:      r.Status === "termine" ? "#4ade80" : "#fbbf24",
                              border:     `1px solid ${r.Status === "termine" ? "rgba(74,222,128,0.3)" : "rgba(251,191,36,0.3)"}`,
                            }}>
                              {r.Status === "termine" ? "✓ Terminé" : r.Status}
                            </span>
                          </td>
                          <td style={S.td}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>
                              {allStacks.map(s => (
                                <span key={s.StackId} style={S.stackBadge}>{s.StackId}</span>
                              ))}
                            </div>
                          </td>
                          <td style={S.td}>
                            <button className="woodoo-voir" style={S.voirBtn}
                              onClick={() => { setDetails(r); setEditMode(false); setEditTarget(null); setEditMasses({}); }}>
                              Voir →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={S.pagination}>
                <button style={{ ...S.pageBtn, opacity: currentPage === 1 ? 0.3 : 1 }}
                  disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹ Précédent</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                    acc.push(p); return acc;
                  }, [])
                  .map((p, i) => p === "..."
                    ? <span key={`d-${i}`} style={S.pageDots}>…</span>
                    : <button key={p} style={{ ...S.pageBtn, ...(p === currentPage ? S.pageBtnActive : {}) }}
                        onClick={() => setCurrentPage(p)}>{p}</button>
                  )}
                <button style={{ ...S.pageBtn, opacity: currentPage === totalPages ? 0.3 : 1 }}
                  disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Suivant ›</button>
              </div>
            )}
          </div>
        </div>

        {/* ════════ MODAL DÉTAIL ════════ */}
        {details && (
          <div style={S.backdrop} onClick={() => { setDetails(null); setEditMode(false); setEditMasses({}); }}>
            <div style={{ ...S.modal, maxWidth: 960 }} onClick={e => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <div style={S.modalTitle}>Conditionnement</div>
                  <div style={S.modalSub}>
                    {details.time_display} · {details.Operateur} · Enceinte {details.NumEnceinte}
                  </div>
                </div>
                <button style={S.modalClose} onClick={() => { setDetails(null); setEditMode(false); setEditMasses({}); }}>✕</button>
              </div>

              {/* Infos session */}
              <div style={S.sectionTitle}>Paramètres session</div>
              <div style={S.mesureGrid}>
                {[
                  ["🏭 Enceinte",     details.NumEnceinte,  null],
                  ["🌡️ Température",  details.Temperature,  "°C"],
                  ["⏱️ Durée",        details.Duree,        "h"],
                  ["🏁 Fin",          details.EndTime,      null],
                  ["✅ Complété à",   details.CompletedAt,  null],
                  ["📦 Nb stacks",    details.NbStacks,     null],
                  ["🪵 Nb plis",      details.NbPlisTotal,  null],
                ].map(([label, val, unit]) => (
                  <div key={label} style={S.mesureCard}>
                    <div style={S.mesureLabel}>{label}</div>
                    <div style={S.mesureVal}>
                      {val ?? "—"}{unit && <span style={S.mesureUnit}> {unit}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Rangées */}
              <div style={S.sectionTitle}>
                Humidité après conditionnement · stacks & plis
              </div>
              {renderRangee("RangeeA", details.RangeeA)}
              {renderRangee("RangeeB", details.RangeeB)}
              {(!details.RangeeA?.length && !details.RangeeB?.length) && (
                <div style={{ textAlign: "center", color: "#64748b", padding: 24 }}>Aucun stack trouvé.</div>
              )}

              <button className="woodoo-btn-lime" style={S.closeBtn}
                onClick={() => { setDetails(null); setEditMode(false); setEditMasses({}); }}>
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; }
  body { font-family: 'Inter', sans-serif; }
  .woodoo-input{background:#0d2233!important;color:#e2e8f0!important;border:1px solid #1e3a50!important;transition:border-color .2s,box-shadow .2s!important;outline:none!important}
  .woodoo-input:focus{border-color:#E6FF3E!important;box-shadow:0 0 0 3px rgba(230,255,62,.15)!important}
  .woodoo-input option{background:#0d2233}
  .woodoo-th:hover{background:#1e3a50!important;color:#E6FF3E!important}
  .woodoo-tr{transition:background .15s!important}
  .woodoo-tr:hover td{background:#1a3248!important}
  .woodoo-voir,.woodoo-edit{transition:background .2s,transform .15s,box-shadow .2s!important}
  .woodoo-voir:hover{background:#fff!important;color:#000!important;box-shadow:0 4px 12px rgba(230,255,62,.3)!important;transform:translateY(-1px)!important}
  .woodoo-edit:hover{background:#93c5fd!important;box-shadow:0 4px 12px rgba(96,165,250,.3)!important;transform:translateY(-1px)!important}
  .woodoo-btn-lime{transition:background .2s,transform .15s,box-shadow .2s!important}
  .woodoo-btn-lime:hover{background:#fff!important;transform:translateY(-1px)!important;box-shadow:0 4px 14px rgba(230,255,62,.35)!important}
  .woodoo-btn-reset{transition:background .2s,transform .15s!important}
  .woodoo-btn-reset:hover{background:#dc2626!important;transform:translateY(-1px)!important}
  .woodoo-scroll::-webkit-scrollbar{height:6px}
  .woodoo-scroll::-webkit-scrollbar-track{background:#0d1b2a}
  .woodoo-scroll::-webkit-scrollbar-thumb{background:#E6FF3E;border-radius:3px}
  @keyframes spin{to{transform:rotate(360deg)}}
  .woodoo-spin{display:inline-block;animation:spin 1s linear infinite}
  @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  .woodoo-tr{animation:fadeIn .2s ease both}
`;

const S = {
  page:         { background:"linear-gradient(160deg,#041e2e 0%,#063549 60%,#052a3e 100%)",minHeight:"100vh",color:"#e2e8f0",fontFamily:"'Inter',sans-serif" },
  wrap:         { padding:"24px 28px",maxWidth:1600,margin:"0 auto" },
  header:       { display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28,flexWrap:"wrap",gap:16 },
  headerTitle:  { fontSize:28,fontWeight:900,background:"linear-gradient(90deg,#fff 0%,#E6FF3E 100%)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" },
  headerSub:    { fontSize:13,color:"#94a3b8",marginTop:4 },
  statsRow:     { display:"flex",gap:12 },
  statCard:     { background:"rgba(255,255,255,0.04)",border:"1px solid #1e3a50",borderRadius:12,padding:"12px 20px",textAlign:"center",backdropFilter:"blur(8px)" },
  statNum:      { fontSize:26,fontWeight:900,color:"#fff" },
  statLabel:    { fontSize:11,color:"#94a3b8",marginTop:2,textTransform:"uppercase",letterSpacing:"0.08em" },
  panel:        { background:"rgba(255,255,255,0.03)",borderRadius:16,border:"1px solid #1e3a50",padding:24,backdropFilter:"blur(12px)" },
  filterBar:    { display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center" },
  filterWrap:   { flex:1,minWidth:160,position:"relative",display:"flex",alignItems:"center" },
  filterIcon:   { position:"absolute",left:10,fontSize:14,zIndex:1,pointerEvents:"none" },
  filterInput:  { width:"100%",padding:"9px 12px 9px 32px",borderRadius:8,fontSize:13,fontWeight:500 },
  exportBtn:    { padding:"9px 18px",borderRadius:8,fontWeight:700,background:"#E6FF3E",border:"none",cursor:"pointer",fontSize:13,color:"#000",whiteSpace:"nowrap" },
  resetBtn:     { padding:"9px 18px",borderRadius:8,fontWeight:700,background:"#ef4444",color:"#fff",border:"none",cursor:"pointer",fontSize:13,whiteSpace:"nowrap" },
  resultCount:  { display:"flex",alignItems:"center",gap:8,marginBottom:14,fontSize:13,fontWeight:600,color:"#E6FF3E" },
  resultDot:    { width:8,height:8,borderRadius:"50%",background:"#E6FF3E",boxShadow:"0 0 6px #E6FF3E" },
  loading:      { textAlign:"center",padding:40,fontSize:16,color:"#94a3b8" },
  errMsg:       { textAlign:"center",padding:20,color:"#f87171",fontWeight:600 },
  tableWrap:    { overflowX:"auto",borderRadius:12,border:"1px solid #1e3a50" },
  table:        { width:"100%",borderCollapse:"collapse",fontSize:13 },
  th:           { padding:"13px 14px",background:"#071e2f",color:"#94a3b8",fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:"0.07em",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap",borderBottom:"1px solid #1e3a50" },
  td:           { padding:"11px 14px",borderBottom:"1px solid #0f2a3d",color:"#cbd5e1",textAlign:"center",whiteSpace:"nowrap",verticalAlign:"middle" },
  tdIndex:      { padding:"11px 14px",borderBottom:"1px solid #0f2a3d",color:"#64748b",fontSize:11,fontWeight:600,textAlign:"center" },
  tdStack:      { color:"#E6FF3E",fontWeight:700,fontFamily:"monospace",fontSize:12 },
  trEven:       { background:"transparent" },
  trOdd:        { background:"rgba(255,255,255,0.015)" },
  sortIcon:     { marginLeft:4,fontSize:10,color:"#64748b" },
  voirBtn:      { padding:"6px 14px",borderRadius:6,cursor:"pointer",background:"#E6FF3E",fontWeight:700,border:"none",fontSize:12,color:"#000",whiteSpace:"nowrap" },
  stackBadge:   { background:"rgba(230,255,62,0.1)",border:"1px solid rgba(230,255,62,0.3)",borderRadius:999,padding:"3px 10px",fontSize:11,fontWeight:700,color:"#E6FF3E",fontFamily:"monospace" },
  stackIdBadge: { background:"rgba(230,255,62,0.15)",border:"1px solid rgba(230,255,62,0.4)",borderRadius:8,padding:"5px 12px",fontSize:13,fontWeight:800,color:"#E6FF3E",fontFamily:"monospace" },
  statusBadge:  { display:"inline-flex",alignItems:"center",padding:"4px 10px",borderRadius:20,fontWeight:700,fontSize:11 },
  rangeeBadge:  { fontSize:13,fontWeight:800,color:"#60a5fa",marginBottom:10,letterSpacing:"0.05em" },
  stackBlock:   { background:"rgba(255,255,255,0.02)",borderRadius:12,border:"1px solid #1e3a50",padding:16,marginBottom:12 },
  stackHeader:  { display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8 },
  pagination:   { display:"flex",justifyContent:"center",alignItems:"center",gap:6,marginTop:18,flexWrap:"wrap" },
  pageBtn:      { padding:"7px 13px",borderRadius:8,border:"1px solid #1e3a50",background:"rgba(255,255,255,0.04)",color:"#cbd5e1",cursor:"pointer",fontWeight:700,fontSize:13 },
  pageBtnActive:{ background:"#E6FF3E",color:"#000",border:"1px solid #E6FF3E" },
  pageDots:     { color:"#64748b",fontSize:14,padding:"0 4px" },
  backdrop:     { position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(4px)",display:"flex",justifyContent:"center",alignItems:"center",padding:20,zIndex:200 },
  modal:        { background:"linear-gradient(160deg,#071e2f 0%,#0a2a40 100%)",color:"#e2e8f0",borderRadius:20,padding:28,width:"95%",maxWidth:900,maxHeight:"90vh",overflowY:"auto",border:"1px solid #1e3a50",boxShadow:"0 25px 60px rgba(0,0,0,0.6)" },
  modalHeader:  { display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12 },
  modalTitle:   { fontSize:22,fontWeight:900,background:"linear-gradient(90deg,#fff,#E6FF3E)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" },
  modalSub:     { fontSize:12,color:"#64748b",marginTop:4 },
  modalClose:   { background:"rgba(255,255,255,0.08)",border:"1px solid #1e3a50",borderRadius:8,color:"#94a3b8",cursor:"pointer",fontSize:16,padding:"4px 10px" },
  sectionTitle: { fontSize:12,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:"#64748b",borderBottom:"1px solid #1e3a50",paddingBottom:8,marginBottom:14,marginTop:24 },
  mesureGrid:   { display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10,marginBottom:8 },
  mesureCard:   { background:"rgba(255,255,255,0.03)",borderRadius:10,border:"1px solid #1e3a50",padding:"14px 16px" },
  mesureLabel:  { fontSize:11,color:"#94a3b8",marginBottom:6,fontWeight:600 },
  mesureVal:    { fontSize:18,fontWeight:800,color:"#fff" },
  mesureUnit:   { fontSize:12,color:"#64748b",fontWeight:400 },
  closeBtn:     { width:"100%",marginTop:24,padding:14,borderRadius:10,fontWeight:900,fontSize:14,background:"#E6FF3E",cursor:"pointer",border:"none",color:"#000",letterSpacing:"0.05em" },
  actionBtns:   { display:"flex",flexDirection:"column",gap:6,alignItems:"center" },
  humStackBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginTop: 12,
    marginBottom: 4,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid rgba(114,183,178,0.35)",
    background: "rgba(114,183,178,0.08)",
  },
};